import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { SECTOR_SYNONYMS } from '../../../util/sectorSynonyms';

import css from './SelectMultipleFilterTree.module.css';

// SECTOR-FILTER-01 — multi-select hierarchical tree filter.
// Used by the SearchPage sidebar (desktop) and SearchFiltersMobile
// (mobile modal). Renders inline (no popover). Tri-state checkboxes
// on parents (vacío / marcado / indeterminado).

const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M2 6l3 3 5-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TriCheckbox = ({ state }) => {
  const className = classNames(css.checkbox, {
    [css.checkboxChecked]: state === 'all',
    [css.checkboxIndeterminate]: state === 'some',
  });
  return (
    <span className={className} aria-hidden="true">
      {state === 'all' ? <CheckIcon /> : state === 'some' ? <DashIcon /> : null}
    </span>
  );
};

// Highlight helper: wraps the matching substring of `text` in a
// <span class={highlight}> so the user sees which characters drove
// the result. Match is accent-insensitive (NFD strip) and
// case-insensitive. We rely on the source labels being precomposed
// (Spanish locale standard) so normalized-index ↔ original-index
// align 1:1; combining-mark sources would shift the match, but those
// are not present in configListing.js.
const Highlight = ({ text, query }) => {
  const q = query ? normalize(query.trim()) : '';
  if (!q) return text;
  const normalized = normalize(text);
  const idx = normalized.indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className={css.highlight}>{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
};

// Filter tree by query: keep parent if its label matches OR any of its
// children match. When a parent matches, keep all children. When only
// children match, keep only the matching ones.
const filterTree = (options, query) => {
  if (!query) return options;
  const q = normalize(query);
  const matched = [];
  for (const parent of options) {
    const matches = option =>
      [option.label, ...(SECTOR_SYNONYMS[option.option] || [])].some(text =>
        normalize(text).includes(q)
      );
    const parentMatches = matches(parent);
    const subs = parent.suboptions || [];
    const matchingSubs = subs.filter(matches);
    if (parentMatches) {
      matched.push(parent);
    } else if (matchingSubs.length > 0) {
      matched.push({ ...parent, suboptions: matchingSubs });
    }
  }
  return matched;
};

// Compute tri-state for a parent based on currently selected slug set.
// Leaf-only parents (no suboptions) are binary.
const getParentState = (parent, selected) => {
  const subs = parent.suboptions || [];
  if (subs.length === 0) {
    return selected.has(parent.option) ? 'all' : 'none';
  }
  let n = 0;
  for (const s of subs) if (selected.has(s.option)) n++;
  if (n === 0 && !selected.has(parent.option)) return 'none';
  if (n === subs.length && selected.has(parent.option)) return 'all';
  return 'some';
};

const stripPrefix = raw => {
  const s = String(raw || '');
  if (s.startsWith('has_any:')) return s.substring(8);
  if (s.startsWith('has_all:')) return s.substring(8);
  return s;
};

const SelectMultipleFilterTree = props => {
  const intl = useIntl();
  const treeId = useId();
  const {
    label,
    options = [],
    queryParamNames,
    initialValues,
    onSubmit,
    searchable = true,
    searchPlaceholder = 'Buscar…',
  } = props;

  const queryParam = queryParamNames?.[0];
  const initialRaw = queryParam ? initialValues?.[queryParam] : null;

  // Preserve exact backend values: a broad stored category is distinct from
  // its leaf activities, and must not silently select all siblings in the UI.
  const validSlugs = useMemo(
    () =>
      new Set(
        options.flatMap(parent => [
          parent.option,
          ...(parent.suboptions || []).map(child => child.option),
        ])
      ),
    [options]
  );
  const initialSet = useMemo(
    () =>
      new Set(
        stripPrefix(initialRaw)
          .split(',')
          .filter(slug => validSlugs.has(slug))
      ),
    [initialRaw, validSlugs]
  );

  const [selected, setSelected] = useState(initialSet);
  const [query, setQuery] = useState('');
  // Expanded set of parent slugs. Defaults to none (all collapsed).
  const [expanded, setExpanded] = useState(() => new Set());
  const inputRef = useRef(null);

  // Re-sync from URL when it changes externally (clear-all sidebar,
  // browser back/forward). Without this the tree state would drift.
  useEffect(() => {
    setSelected(initialSet);
  }, [initialSet]);

  const filtered = useMemo(() => filterTree(options, query), [options, query]);

  // Derived expansion: when searching, expand every visible parent so
  // matches surface immediately. When not searching, defer to user
  // clicks (`expanded` state). Clearing the query restores the
  // pre-search expanded state — parents auto-expanded by search
  // collapse back unless the user had toggled them manually.
  const isExpanded = useCallback(parentOption => (query ? true : expanded.has(parentOption)), [
    expanded,
    query,
  ]);

  const apply = useCallback(
    nextSet => {
      setSelected(nextSet);
      onSubmit({
        [queryParam]: [...nextSet].filter(slug => validSlugs.has(slug)).join(',') || null,
      });
    },
    [validSlugs, onSubmit, queryParam]
  );

  // Toggle a parent: state==='all' → unselect all (parent + children);
  // any other state → select all (parent + children).
  const toggleParent = useCallback(
    parent => {
      const next = new Set(selected);
      const state = getParentState(parent, selected);
      const subs = parent.suboptions || [];
      if (state === 'all') {
        next.delete(parent.option);
        for (const s of subs) next.delete(s.option);
      } else {
        next.add(parent.option);
        for (const s of subs) next.add(s.option);
      }
      apply(next);
    },
    [apply, selected]
  );

  // Each stored value is independently selectable; never change hidden values.
  const toggleChild = useCallback(
    (child, parent) => {
      const next = new Set(selected);
      if (next.has(child.option)) next.delete(child.option);
      else next.add(child.option);
      apply(next);
    },
    [apply, selected]
  );

  const toggleExpand = useCallback(parentOption => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(parentOption)) next.delete(parentOption);
      else next.add(parentOption);
      return next;
    });
  }, []);

  const clearQuery = useCallback(() => {
    setQuery('');
    // Refocus the input so the user can keep typing without an extra
    // click. The pre-search expanded state is preserved automatically
    // because we never mutated it.
    inputRef.current?.focus();
  }, []);

  return (
    <div className={css.root} role="group" aria-label={label}>
      <div className={css.filterHeader}>
        {/* FILTER-ACTIVE-INDICATOR: título resaltado cuando hay selección. */}
        <p
          className={classNames(css.label, {
            [css.labelActive]: selected.size > 0,
          })}
        >
          {label}
        </p>
      </div>

      {searchable ? (
        <div className={css.searchWrapper}>
          <input
            ref={inputRef}
            type="text"
            className={css.searchInput}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className={css.searchClear}
              onClick={clearQuery}
              aria-label={intl.formatMessage({
                id: 'SelectMultipleFilterTree.clearSearch',
              })}
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}

      <ul className={css.list}>
        {filtered.length === 0 ? (
          <li className={css.empty} role="status">
            {intl.formatMessage({ id: 'SelectMultipleFilterTree.noResults' })}
          </li>
        ) : null}
        {filtered.map(parent => {
          const subs = parent.suboptions || [];
          const hasSubs = subs.length > 0;
          const state = getParentState(parent, selected);
          const expandedNow = hasSubs && isExpanded(parent.option);

          return (
            <li key={parent.option} className={css.parent}>
              <div className={css.row}>
                <button
                  type="button"
                  className={classNames(css.parentRow, {
                    [css.parentRowFull]: !hasSubs,
                  })}
                  onClick={() => toggleParent(parent)}
                  role="checkbox"
                  aria-checked={state === 'some' ? 'mixed' : state === 'all'}
                >
                  <TriCheckbox state={state} />
                  <span className={css.labelText}>
                    <Highlight text={parent.label} query={query} />
                  </span>
                </button>
                {hasSubs && !query ? (
                  <button
                    type="button"
                    className={css.expandBtn}
                    onClick={() => toggleExpand(parent.option)}
                    aria-label={intl.formatMessage(
                      {
                        id: expandedNow
                          ? 'SelectMultipleFilterTree.collapseSector'
                          : 'SelectMultipleFilterTree.expandSector',
                      },
                      { sector: parent.label }
                    )}
                    aria-controls={expandedNow ? `${treeId}-${parent.option}` : undefined}
                    aria-expanded={expandedNow}
                  >
                    <span aria-hidden="true">{expandedNow ? '▾' : '▸'}</span>
                  </button>
                ) : null}
              </div>

              {hasSubs && expandedNow ? (
                <ul id={`${treeId}-${parent.option}`} className={css.subList}>
                  <li className={css.childRow}>
                    <button
                      type="button"
                      className={css.childLabel}
                      role="checkbox"
                      aria-checked={selected.has(parent.option)}
                      onClick={() => toggleChild(parent, parent)}
                    >
                      <TriCheckbox state={selected.has(parent.option) ? 'all' : 'none'} />
                      <span className={css.labelText}>
                        {intl.formatMessage(
                          { id: 'SelectMultipleFilterTree.wholeCategory' },
                          { sector: parent.label }
                        )}
                      </span>
                    </button>
                  </li>
                  {subs.map(child => {
                    const childChecked = selected.has(child.option);
                    return (
                      <li key={child.option} className={css.childRow}>
                        <button
                          type="button"
                          className={css.childLabel}
                          onClick={() => toggleChild(child, parent)}
                          role="checkbox"
                          aria-checked={childChecked}
                        >
                          <TriCheckbox state={childChecked ? 'all' : 'none'} />
                          <span className={css.labelText}>
                            <Highlight text={child.label} query={query} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SelectMultipleFilterTree;
